import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Row, Form, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCompanyStore, useUserStore, useCartStore } from 'stores';
import { validateEmailOrPhone } from 'helpers';
import { getAllBookStores } from 'api/company';
import { CustomSelectComponent } from 'components/CustomSelectComponent';
import { InputCustom } from 'components/input';

const OrderPickup = () => {
  const { t } = useTranslation(['shipping']);

  const { name: empresa, direccion_completa, id: bookstoreId, is_single_company } = useCompanyStore();
  const { firstName, email, id: userId, setPickUpLibrary, fetchUserInfo, updateUserInfo } = useUserStore();
  const { setContactInfo } = useCartStore();

  const [options, setOptions] = useState([]);
  const [isDefaultLibraryPicked, setIsDefaultLibraryPicked] = useState(true);
  const [error, setError] = useState({ name: '', email: '' });
  const searchTimeoutRef = useRef(null);

  const [contactInfor, setContactInfor] = useState({
    name: firstName || '',
    email: email || '',
  });

  const searchBookStores = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setOptions([]);
      return;
    }

    try {
      const { status, data } = await getAllBookStores({ 
        search: searchTerm,
        page: 1,
        page_size: 20
      });
      if (status === 200 && data?.results) {
        const bookStores = data.results.map((store) => ({
          value: store?.id,
          label: store?.name,
          subtitle: store?.direccion,
        }));
        setOptions(bookStores);
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error('Error fetching book stores:', error);
      setOptions([]);
    }
  }, []);

  const handleInputChange = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm || searchTerm.length < 2) {
      setOptions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchBookStores(searchTerm);
    }, 500);
  }, [searchBookStores]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setContactInfor((prev) => ({
      ...prev,
      name: firstName || '',
      email: email || '',
    }));
  }, [firstName, email]);

  const handleBookstoreSelection = (selectedOption) => {
    const storeId = selectedOption?.value || selectedOption;
    setPickUpLibrary(storeId);
    setIsDefaultLibraryPicked(false);
  };

  const choosePickUpLibrary = () => {
    setIsDefaultLibraryPicked(!isDefaultLibraryPicked);
    if (isDefaultLibraryPicked === false) {
      setPickUpLibrary(bookstoreId);
    }
  };
  const handleContactInfoChange = (field, value) => {
    setContactInfor((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'email') {
      const validation = validateEmailOrPhone(value);
      if (!validation.isValid) {
        setError((prev) => ({ ...prev, email: t('invalidEmail') }));
      } else {
        setError((prev) => ({ ...prev, email: '' }));
      }
    }

    if (field === 'name' && value.trim()) {
      setError((prev) => ({ ...prev, name: '' }));
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const hasNameChange = contactInfor.name.trim() !== (firstName || '');
      const hasEmailChange = contactInfor.email !== (email || '');

      if (!hasNameChange && !hasEmailChange) return;

      const updateData = {};

      if (hasNameChange) {
        updateData.first_name = contactInfor.name.trim();
      }

      if (hasEmailChange && !error.email) {
        const validation = validateEmailOrPhone(contactInfor.email);
        if (validation.isValid && validation.type === 'email') {
          updateData.email = contactInfor.email;
        }
      }

      if (Object.keys(updateData).length > 0) {
        try {
          await updateUserInfo({ data: updateData, id: userId });
          fetchUserInfo();
        } catch (err) {
          console.error('Failed to update user info:', err);
        }
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [contactInfor.name, contactInfor.email, firstName, email, error.email, userId, updateUserInfo, fetchUserInfo]);

  useEffect(() => {
    setContactInfo(contactInfor);
  }, [contactInfor, setContactInfo]);

  return (
    <>
      <h4 className="mx-4 py-3">{t('chooseWherePickup')}</h4>
      <Row className="mx-3">
        <Col lg={6} className="mt-2 d-flex libraryCheck">
          <Form.Check
            type="radio"
            checked={isDefaultLibraryPicked}
            onChange={choosePickUpLibrary}
            name="pickup-selection"
            className="border-highlight"
          />
          <div className="px-2">
            <p>{empresa}</p>
            <p className="m-0">{direccion_completa}</p>
          </div>
        </Col>
        {!is_single_company && (
          <Col lg={6} className="mt-2 d-flex">
            <Form.Check
              type="radio"
              checked={!isDefaultLibraryPicked}
              onChange={choosePickUpLibrary}
              name="pickup-selection"
              className="border-highlight"
            />
            <Col className="px-2 d-flex flex-column w-100">
              <p>{t('anotherBookstore')}</p>
              <CustomSelectComponent
                handleSelection={handleBookstoreSelection}
                options={options}
                resetValue={isDefaultLibraryPicked}
                isDisabled={isDefaultLibraryPicked}
                onInputChange={handleInputChange}
                dataTestId="another Library"
              />
              {!isDefaultLibraryPicked && <small className="text-muted mt-2">{t('chooseBookstorePickup')}</small>}
            </Col>
          </Col>
        )}
      </Row>
      <Row className="mx-3">
        <Col lg={12} className="mt-3 py-2">
          <p className="m-0">{t('contactInfoMessage')}</p>
        </Col>
        <Col lg={6} className="mt-1">
          <InputCustom
            label={`${t('name')}:`}
            type="text"
            value={contactInfor.name}
            onChange={(e) => handleContactInfoChange('name', e.target.value)}
            placeholder={t('enterName')}
            className="styleInput w-100"
          />
          {error.name && <small className="text-danger">{error.name}</small>}
        </Col>
        <Col lg={6} className="mt-1">
          <InputCustom
            label={`${t('email')}:`}
            type="text"
            value={contactInfor.email}
            onChange={(e) => handleContactInfoChange('email', e.target.value)}
            placeholder={t('enterEmail')}
            className="styleInput w-100"
          />
          {error.email && <small className="text-danger">{error.email}</small>}
        </Col>
      </Row>
    </>
  );
};

export default OrderPickup;
